import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import api from "../lib/api";
import { initTelemetry, setTelemetryTenant } from "../lib/telemetry";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Guard so telemetry init runs exactly once per resolved user session.
  const telemetryInitialisedFor = useRef(null);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/client/me");
      setUser(res.data);
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem("csrf_token");
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Initialise telemetry once per session after user resolves.
  // Inert when the backend returns no connection string.
  useEffect(() => {
    if (!user || telemetryInitialisedFor.current === user.tenant_id) return;
    telemetryInitialisedFor.current = user.tenant_id;
    initTelemetry().then(() => setTelemetryTenant(user.tenant_id, user.role));
  }, [user]);

  const login = async (email, password, source) => {
    const res = await api.post("/client/login", {
      email,
      password,
      ...(source ? { source } : {}),
    });
    if (res.data.requires_2fa) {
      return res.data;
    }
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("csrf_token", res.data.csrf_token);
    await checkAuth();
    return res.data;
  };

  const verify2FA = async (email, code) => {
    const res = await api.post("/client/login/verify-2fa", { email, code });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("csrf_token", res.data.csrf_token);
    await checkAuth();
    return res.data;
  };

  const logout = async () => {
    // Revoke token server-side before clearing locally
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await fetch("/api/client/logout", {
          method: "POST",
          headers: { "Authorization": `Bearer ${token}` },
        });
      }
    } catch {
      // Best-effort — proceed with local logout even if server call fails
    }
    localStorage.removeItem("token");
    localStorage.removeItem("csrf_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verify2FA, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
